import moment from "moment";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import {
	PacientesProps,
	UserProps,
} from "../../interfaces/pacientes.interface";
import { persistStorage } from "../../utils/base_async_storage";
import { TypesErrorRequest } from "../../utils/types";
import { AppActions } from "../ducks/app";
import { ConfigsActions, Types } from "../ducks/configs";
import history from "../../routes/history";
import { sendErrorSentryOrLocalHost } from "../../utils/funcs";
import { apiFetch, apiFetchWrite } from "../../services/httpClient";

interface DinamosBlocked {
	DINAMO_UUID: string;
	tipo_bloqueio: string | null;
}

function* getConfigs(): any {
	const user_logado: UserProps = yield select((state: any) => state.auth.user);
	const language: any = yield select((state: any) => state.configs.language);
	const version: any = yield select((state: any) => state.configs.version);
	const dat = {
		TOKEN: user_logado.token,
		ID_USUARIO: user_logado?.ID_USUARIO,
		txPlatform: "Web",
		txVersao: version,
		txIdioma: language.toLowerCase(),
	};

	async function apiCall() {
		try {
			const requestOptions: any = {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + user_logado.token,
				},
				body: JSON.stringify(dat),
			};
			const res = await apiFetch(
				`${process.env.REACT_APP_BASE_URL_V7}syncs/configs`,
				requestOptions
			);
			const response = await res.json();
			return response;
		} catch (e) {
			sendErrorSentryOrLocalHost(e, {
				message: "Erro ao buscar configurações - getConfigs",
				tags: {
					endpoint: "/syncs/configs",
					file: "src/store/sagas/config.ts",
				},
			});
			throw e;
		}
	}

	function* createConfigUser(data: any) {
		yield put(ConfigsActions.set_alerta_sonoro(data.CONFIG_EXER_SOM));
		yield put(ConfigsActions.set_alerta_vibratorio(data.CONFIG_EXER_VIBRAR));
		yield put(ConfigsActions.set_contagem_regresiva(data.CONFIG_EXER_321));
		yield put(
			ConfigsActions.set_tempo_contracao(data.CONFIG_EXER_CONTRACAO_FLAG)
		);
		yield put(
			ConfigsActions.set_intervalo_descanso(data.CONFIG_EXER_DESCANSO_FLAG)
		);
		yield put(
			ConfigsActions.set_tempo_contracao_timer(data.CONFIG_EXER_CONTRACAO)
		);
		yield put(
			ConfigsActions.set_intervalo_descanso_timer(data.CONFIG_EXER_DESCANSO)
		);
		yield put(ConfigsActions.set_config_funcional(data.CONFIG_FUNCIONAL));
	}

	try {
		const data = yield call(apiCall);
		yield call(persistStorage, {
			res: data.config_user,
			tag: "config_user",
		});
		yield call(persistStorage, {
			res: data.config_app,
			tag: "config_app",
		});
		yield call(persistStorage, {
			res: data.config_servicos,
			tag: "config_servicos",
		});
		yield call(createConfigUser, data.config_user);

		yield put(ConfigsActions.set_config_app(data.config_app));
		yield put(ConfigsActions.set_config_servico(data.config_servicos));
		yield put(
			ConfigsActions.set_config_inadimplencia(data.config_inadimplencia)
		);
		yield put(
			ConfigsActions.set_config_lista_usuarios(data.config_lista_usuarios)
		);
		yield put(ConfigsActions.set_config_relatorio(data.config_relatorios));
		yield put(
			ConfigsActions.set_quantidade_emissao_relatorios(
				data.quantidade_emissao_relatorios
			)
		);
		yield put(ConfigsActions.request_evaluated_patients());

		const modeWeb = data?.config_servicos?.SERVICO_WEB;
		if (modeWeb == false) {
			history.push("/aviso");
			yield put(ConfigsActions.set_remove_button_close(true));
			return;
		}

		// if (data?.config_servicos?.SERVICO_BLOQUEADO == "S") {
		// 	yield put(AppActions.set_modal_inadimplente(true));
		// }
	} catch (e) {}
}

function* CheckEmiterFreeReport(user?: UserProps): any {
	const patient: PacientesProps = yield select(
		(state: any) => state.app.is_open_paciente
	);
	if (!patient?.ID_USUARIO) {
		return;
	}

	const authenticatedUser: UserProps = yield select(
		(state: any) => state.auth.user
	);
	const user_logado = user || authenticatedUser;
	if (!user_logado?.token) {
		return;
	}

	const requestOptions = {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${user_logado.token}`,
		},
		body: JSON.stringify({
			TOKEN: user_logado.token,
			ID_USUARIO: patient?.ID_USUARIO,
		}),
	};

	async function apiCall() {
		try {
			const response = await apiFetch(
				`${process.env.REACT_APP_BASE_URL_V7}syncs/verifica_relatorio_free`,
				requestOptions
			);

			return await response.json();
		} catch (e) {
			sendErrorSentryOrLocalHost(e, {
				message: "Erro ao verificar relatório gratuito",
				tags: {
					endpoint: "/syncs/verifica_relatorio_free",
					file: "src/store/sagas/config.ts",
				},
			});

			throw e;
		}
	}

	try {
		const data: any = yield call(apiCall);

		if (data.status === "success") {
			yield put(ConfigsActions.set_free_report_data(data));
		}
	} catch (error) {
		sendErrorSentryOrLocalHost(error, {
			message: "Erro ao verificar relatório gratuito",
			tags: {
				endpoint: "/syncs/verifica_relatorio_free",
				file: "src/store/sagas/config.ts",
			},
		});
	}
}

function* getPlanServices({ payload }: any): any {
	const authenticatedUser: UserProps = yield select(
		(state: any) => state.auth.user
	);
	const user_logado: UserProps = payload || authenticatedUser;

	if (!user_logado?.token) {
		yield put(
			ConfigsActions.set_plan_services_failure("Usuário não autenticado")
		);
		return;
	}

	const requestOptions = {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${user_logado.token}`,
		},
		body: JSON.stringify({
			TOKEN: user_logado.token,
		}),
	};

	async function apiCall() {
		try {
			const response = await apiFetch(
				`${process.env.REACT_APP_BASE_URL_V7}syncs/servicos`,
				requestOptions
			);

			return await response.json();
		} catch (e) {
			sendErrorSentryOrLocalHost(e, {
				message: "Erro ao buscar serviços",
				tags: {
					endpoint: "/syncs/servicos",
					file: "src/store/sagas/config.ts",
				},
			});

			throw e;
		}
	}

	try {
		const data: any = yield call(apiCall);

		if (data.status === TypesErrorRequest.SUCCESS && data.code === 0) {
			yield put(
				ConfigsActions.set_data_plan_services(data.subscription, data.plans)
			);

			yield call(CheckEmiterFreeReport, user_logado);
			return;
		}

		yield put(ConfigsActions.set_plan_services_failure(data));
	} catch (error) {
		yield put(ConfigsActions.set_plan_services_failure(error));
		sendErrorSentryOrLocalHost(error, {
			message: "Erro ao buscar serviços",
			tags: {
				endpoint: "/syncs/servicos",
				file: "src/store/sagas/config.ts",
			},
		});
	}
}

function* configSalvar({ payload }: any): any {
	const user_logado: UserProps = yield select((state: any) => state.auth.user);
	const current_language: any = yield select(
		(state) => state.configs.current_language
	);

	const dat = {
		TOKEN: user_logado.token,
		txDataHora: moment(new Date()).format("YYYY-MM-DD HH:mm:ss").toString(),
		...payload,
	};
	async function apiCall() {
		try {
			const requestOptions: any = {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + user_logado.token,
				},
				body: JSON.stringify(dat),
			};
			const res = await apiFetchWrite(
				`${process.env.REACT_APP_BASE_URL_V7}configs/usuario_salvar`,
				requestOptions
			);
			const response = await res.json();
			return response;
		} catch (e) {
			sendErrorSentryOrLocalHost(e, {
				message: "Erro ao salvar config - configSalvar",
				tags: {
					endpoint: "/configs/usuario_salvar",
					file: "src/store/sagas/config.ts",
				},
				payload: payload,
			});
			throw e;
		}
	}

	try {
		const response = yield call(apiCall);
		let message: any = "";
		if (response.status == TypesErrorRequest.SUCCESS) {
			yield put(ConfigsActions.config_request());
		}
		if (response.status == TypesErrorRequest.FAIL) {
			if (response.code == -1) {
				message = current_language?.atualizacao_falhou2;
			}

			yield put(
				AppActions.set_toast_visible({
					status_toast: true,
					type_toast: TypesErrorRequest.FAIL,
					message_toast: message,
				})
			);
		}
	} catch (e) {}
}
function* addDino({ payload }: any) {
	// const dinamo_mac_address: string = yield select(
	// 	(state: any) => state.configs.mac_address
	// );
	// const dinamo_uuid: string = yield select(
	// 	(state: any) => state.configs.dinamo_uuid
	// );
	// const device_name: string = yield select(
	// 	(state: any) => state.configs.device_name
	// );
	const user_logado: UserProps = yield select((state: any) => state.auth.user);

	async function apiCall() {
		if (payload?.device_name) {
			const data_request = {
				TOKEN: user_logado.token,
				txDataHora: moment(new Date()).format("YYYY-MM-DD HH:mm:ss").toString(),
				DINAMO_NOME: payload?.device_name,
				DINAMO_MAC: payload?.mac_address ? payload?.mac_address : " ",
				DINAMO_UUID: payload?.dinamo_uuid ? payload?.dinamo_uuid : " ",
			};
			const requestOptions: any = {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + user_logado.token,
				},
				body: JSON.stringify(data_request),
			};
			try {
				const data = await apiFetchWrite(
					`${process.env.REACT_APP_BASE_URL_V7}dinamos/salvar`,
					requestOptions
				);

				const response = await data.json();
				return response;
			} catch (e) {
				sendErrorSentryOrLocalHost(e, {
					message: "Erro ao adicionar dino - addDino",
					tags: {
						endpoint: "dinamos/salvar",
						file: "src/store/sagas/config.ts",
					},
				});
			}
		}
	}

	try {
		const response: {
			code: number;
			message: string;
			status: string;
		} = yield call(apiCall);
		if (response.status != TypesErrorRequest.SUCCESS) {
			console.log(
				"~ file: config.ts:214 ~ function*addDino ~ response:",
				response.message
			);
		}
	} catch (error) {}
}

function* getBlockedDinos() {
	const user_logado: UserProps = yield select((state: any) => state.auth.user);

	async function apiCall() {
		const data_request = {
			TOKEN: user_logado.token,
		};
		const requestOptions: any = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + user_logado.token,
			},
			body: JSON.stringify(data_request),
		};
		try {
			const data = await apiFetch(
				`${process.env.REACT_APP_BASE_URL_V7}dinamos/verificar`,
				requestOptions
			);
			const response = await data.json();
			return response;
		} catch (e) {
			sendErrorSentryOrLocalHost(e, {
				message: "Erro ao buscar dinos bloqueados - getBlockedDinos",
				tags: {
					endpoint: "/dinamos/verificar",
					file: "src/store/sagas/config.ts",
				},
			});
		}
	}

	try {
		const response: {
			code: number;
			dinamos_invalidos: Array<DinamosBlocked>;
			status: string;
		} = yield call(apiCall);
		if (response.status === TypesErrorRequest.SUCCESS) {
			yield put(AppActions.set_blocked_dinamos(response.dinamos_invalidos));
		} else {
			console.log(" ~ file: config.ts:245 ~ try ~ error:");
		}
	} catch (error) {}
}

function* BlockDino() {
	const blocked_dinamos: Array<DinamosBlocked> = yield select(
		(state: any) => state.app.dinamos_blocked
	);
	const dinamo_mac_address: string = yield select(
		(state: any) => state.configs.mac_address
	);

	for (const i in blocked_dinamos) {
		if (blocked_dinamos[i].DINAMO_UUID === dinamo_mac_address) {
			yield put(AppActions.set_modal_bloqueio_dino(true));
		}
	}
}
function* getNumberOfEvaluatedPatients() {
	const user_logado: UserProps = yield select((state: any) => state.auth.user);

	const data_request = {
		TOKEN: user_logado.token,
	};
	const requestOptions = {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: "Bearer " + user_logado.token,
		},
		body: JSON.stringify(data_request),
	};

	async function apiCall() {
		try {
			const data = await apiFetch(
				`${process.env.REACT_APP_BASE_URL_V7}syncs/pacientes_avaliados`,
				requestOptions
			);

			const response = await data.json();

			return response;
		} catch (e) {
			sendErrorSentryOrLocalHost(e, {
				message:
					"Erro ao sincronizar pacientes avaliados - getNumberOfEvaluatedPatients",
				tags: {
					endpoint: "/syncs/pacientes_avaliados",
					file: "src/store/sagas/config.ts",
				},
			});
		}
	}

	try {
		const data: { pacientes_avaliados: number[] } = yield call(apiCall);

		yield call(persistStorage, {
			res: data?.pacientes_avaliados,
			tag: "evaluated_patients",
		});
	} catch (error) {}
}

function* setModalVisualization({ payload }: any) {
	const user_logado: UserProps = yield select((state: any) => state.auth.user);

	const data_request = {
		TOKEN: user_logado.token,
		...payload,
	};

	const requestOptions = {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: "Bearer " + user_logado.token,
		},
		body: JSON.stringify(data_request),
	};

	async function apiCall() {
		try {
			const data = await apiFetchWrite(
				`${process.env.REACT_APP_BASE_URL_V7}modais/visualizar`,
				requestOptions
			);

			const response = await data.json();

			return response;
		} catch (e) {
			sendErrorSentryOrLocalHost(e, {
				message: "Erro ao sincronizar dados do modal - setModalVisualization",
				tags: {
					endpoint: "/modais/visualizar",
					file: "src/store/sagas/config.ts",
				},
			});
		}
	}

	try {
		yield call(apiCall);
	} catch (error) {}
}

export function* ConfigSaga(): any {
	return yield all([
		takeLatest(Types.CONFIG_REQUEST, getConfigs),
		takeLatest(Types.PLAN_SERVICES_REQUEST, getPlanServices),
		takeLatest(Types.CONFIG_EDIT_REQUEST, configSalvar),
		takeLatest(Types.SET_IDENTIFICATION_DINAMO, addDino),
		takeLatest(Types.BLOCK_DINO_REQUEST, getBlockedDinos),
		takeLatest(Types.SET_IDENTIFICATION_DINAMO, BlockDino),
		takeLatest(Types.VERIFY_BLOCKED_DINO, BlockDino),
		takeLatest(Types.REQUEST_EVALUATED_PATIENTS, getNumberOfEvaluatedPatients),
		takeLatest(Types.SET_MODAL_VISUALIZATION, setModalVisualization),
	]);
}
